import { PhoneNumberUtil } from 'google-libphonenumber'
import { useState, useEffect } from 'react'

import moment from 'moment'

const OHStaffDetails = ({ requestData, setRequestData, formData, setFormData, workingHoursDropdown, setWorkingHoursDropdown, files, setFiles }) => {

    const [validation, setValidation] = useState({
        phone: {
            isValid: false,
        },
        email: {
            isValid: false,
        },
        showValidationTooltip: false,
        textareaFocused: {},
    })

    useEffect(() => {
        if (formData?.staff_details?.phone_number) {
            let phoneNumber = formData.staff_details.phone_number

            if (phoneNumber.startsWith('+44')) {
                phoneNumber = phoneNumber.replace('+44', '')
                setFormData(prevFormData => ({
                    ...prevFormData,
                    staff_details: {
                        ...prevFormData.staff_details,
                        phone_number: phoneNumber
                    }
                }))
                return
            }

            const isValidNumber = validatePhoneNumber('+44' + phoneNumber)
            setValidation((prevValidation) => ({
                ...prevValidation,
                phone: {
                    isValid: isValidNumber,
                },
            }))
        }
        if (formData?.staff_details?.staff_email && formData.staff_details.staff_email.length > 0) {
            const isValidEmailAddress = validateEmail(formData.staff_details.staff_email)
            setValidation((prevValidation) => ({
                ...prevValidation,
                email: {
                    isValid: isValidEmailAddress,
                },
            }))
        }
    }, [])

    useEffect(() => {
        if (formData?.staff_details?.phone_number && !formData.staff_details.phone_number.startsWith('+44')) {
            const isValidNumber = validatePhoneNumber('+44' + formData.staff_details.phone_number)
            setValidation((prevValidation) => ({
                ...prevValidation,
                phone: {
                    isValid: isValidNumber,
                },
            }))
        }
    }, [formData?.staff_details?.phone_number])

    useEffect(() => {
        if (requestData && !requestData.v2 && requestData.request_info_array?.staff_details &&
            (!formData.staff_details || Object.keys(formData.staff_details).length <= 2)) {
            const legacyStaffDetails = requestData.request_info_array.staff_details

            const migratedStaffDetails = {
                ...formData.staff_details,
                staff_email: legacyStaffDetails.staff_email || '',
                date_of_birth: legacyStaffDetails.date_of_birth || '',
                role: legacyStaffDetails.role || '',
                contact_name: legacyStaffDetails.contact_name || '',
                contact_email: legacyStaffDetails.contact_email || ''
            }

            const migratedTopLevelData = {
                school_name: legacyStaffDetails.school_name || '',
                school_postcode: legacyStaffDetails.school_postcode || '',
                org_id: legacyStaffDetails.org_id || ''
            }

            if (legacyStaffDetails.staff_name) {
                const nameParts = legacyStaffDetails.staff_name.split(' ')
                migratedStaffDetails.first_name = nameParts[0] || ''
                migratedStaffDetails.last_name = nameParts.slice(1).join(' ') || ''
                migratedStaffDetails.staff_name = legacyStaffDetails.staff_name
            }

            if (legacyStaffDetails.phone_number) {
                let phoneNumber = legacyStaffDetails.phone_number
                if (phoneNumber.startsWith('+44')) {
                    phoneNumber = phoneNumber.replace('+44', '')
                }
                migratedStaffDetails.phone_number = phoneNumber
            }

            if (legacyStaffDetails.working_hours) {
                setWorkingHoursDropdown('PT')
                migratedStaffDetails.working_hours = legacyStaffDetails.working_hours
            } else {
                setWorkingHoursDropdown('FT')
            }

            setFormData(prevFormData => ({
                ...prevFormData,
                staff_details: migratedStaffDetails,
                ...migratedTopLevelData
            }))
        }
    }, [requestData?.id, requestData?.v2])

    const validatePhoneNumber = (phoneNumber) => {
        const phoneNumberUtil = PhoneNumberUtil.getInstance()

        try {
            const parsedNumber = phoneNumberUtil.parse(phoneNumber, '+44')
            const isValidNumber = phoneNumberUtil.isValidNumber(parsedNumber)
            return isValidNumber
        } catch (e) {
            return false
        }
    }

    const validateEmail = (email) => {
        if (!email || email.trim() === '') return true // Consider empty as valid to avoid showing error immediately
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const handlePhoneNumberChange = (event) => {
        let number = event.target.value

        if (number.length === 11 && number.startsWith('0')) {
            number = number.replace(/^0/, '')
        }

        setFormData({
            ...formData,
            staff_details: {
                ...formData?.staff_details,
                phone_number: number,
            },
        })

        const isValidNumber = validatePhoneNumber('+44' + number)
        setValidation((prevValidation) => ({
            ...prevValidation,
            phone: {
                isValid: isValidNumber,
            },
        }))
    }

    const handleInputUpdate = (field, value) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            staff_details: {
                ...prevFormData?.staff_details,
                [field]: value,
            },
        }))

        if (field === 'staff_email' && value.length > 0) {
            const isValidEmailAddress = validateEmail(value)
            setValidation((prevValidation) => ({
                ...prevValidation,
                email: {
                    isValid: isValidEmailAddress,
                },
            }))
        }
    }

    return (
        <div className="border border-gray-200 rounded-lg">
            <div
                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
            >
                <label className="block text-base font-semibold text-[#02ADAD]">
                    Staff Member Details
                </label>
            </div>
            <div className={`overflow-hidden transition-all duration-700 ease-in-out`}>
                <div className="p-3">
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-blue-700">
                                    We collect the staff member's details so our occupational health team can reach out directly if additional information is needed to provide the best possible support and advice.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700" htmlFor="first_name">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                required
                                className="border rounded-md py-1.5 px-3 w-full mt-1 text-sm"
                                onChange={(e) => handleInputUpdate('first_name', e.target.value)}
                                value={formData?.staff_details?.first_name || ''}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700" htmlFor="last_name">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                required
                                className="border rounded-md py-1.5 px-3 w-full mt-1 text-sm"
                                onChange={(e) => handleInputUpdate('last_name', e.target.value)}
                                value={formData?.staff_details?.last_name || ''}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col mt-3">
                        <label className="text-sm font-medium text-gray-700" htmlFor="staff_email">
                            Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            id="staff_email"
                            className="border rounded-md py-1.5 px-3 mt-1 text-sm"
                            required
                            onChange={(e) => handleInputUpdate('staff_email', e.target.value)}
                            value={formData?.staff_details?.staff_email || ''}
                        />
                        {formData?.staff_details?.staff_email?.length > 0 && (
                            <>
                                {validation?.email?.isValid ? (
                                    <span className="text-green-500 text-sm">
                                        Valid email address
                                    </span>
                                ) : (
                                    <span className="text-red-600 text-sm">
                                        Invalid email address
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex flex-col mt-3">
                        <label className="text-sm font-medium text-gray-700" htmlFor="date_of_birth">
                            Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="date_of_birth"
                            className="border rounded-md py-1.5 px-3 mt-1 text-sm"
                            required
                            max={moment().format('YYYY-MM-DD')}
                            onChange={(e) => handleInputUpdate('date_of_birth', e.target.value)}
                            value={formData?.staff_details?.date_of_birth || ''}
                        />
                    </div>
                    <div className="flex flex-col mt-3">
                        <label className="text-sm font-medium text-gray-700" htmlFor="phone_number">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center w-full">
                            <p className="border bg-gray-200 rounded-l-md py-1.5 px-3 mt-1 text-sm">+44</p>
                            <input
                                type="number"
                                id="phone_number"
                                className="border rounded-r-md py-1.5 px-3 mt-1 w-full text-sm"
                                required
                                onChange={(e) => handlePhoneNumberChange(e)}
                                value={formData?.staff_details?.phone_number || ''}
                            />
                        </div>
                        {formData?.staff_details?.phone_number?.length > 0 && (
                            <>
                                {validation?.phone?.isValid ? (
                                    <span className="text-green-500 text-sm">
                                        Valid phone number
                                    </span>
                                ) : (
                                    <span className="text-red-600 text-sm">
                                        Invalid phone number
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex flex-col mt-3">
                        <label className="text-sm font-medium text-gray-700" htmlFor="role">
                            Role Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="role"
                            className="border rounded-md py-1.5 px-3 mt-1 text-sm"
                            required
                            onChange={(e) => handleInputUpdate('role', e.target.value)}
                            value={formData?.staff_details?.role || ''}
                        />
                    </div>
                    <div className="flex flex-col mt-3">
                        <label className="text-sm font-medium text-gray-700" htmlFor="working_hours_dropdown">
                            Working Hours <span className="text-red-500">*</span>
                        </label>
                        <select
                            type="text"
                            id="working_hours_dropdown"
                            className="border rounded-md py-1.5 px-3 mt-1 text-sm"
                            required
                            onChange={(e) => setWorkingHoursDropdown(e.target.value)}
                            value={workingHoursDropdown || ''}

                        >
                            <option disabled value="">Please Select</option>
                            <option value="FT">Full Time</option>
                            <option value="PT">Part Time</option>
                        </select>
                        {workingHoursDropdown === "PT" &&
                            <input
                                type="number"
                                id="working_hours"
                                placeholder='Please Enter Number of Working Hours'
                                className="border rounded-md py-3 px-3 mt-1 text-sm"
                                required
                                onChange={(e) => handleInputUpdate('working_hours', e.target.value)}
                                value={formData?.staff_details?.working_hours || ''}
                            />
                        }
                    </div>
                    <div className="flex flex-col mt-3 space-y-2">
                        <label className="text-sm font-medium text-gray-700" htmlFor="role">
                            Staff member is still in work at the time of referral<span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center">
                            <input
                                type="radio"
                                id="is_in_work"
                                name="inWork"
                                className="mr-2"
                                onChange={(e) => handleInputUpdate('in_work', true)}
                                checked={formData?.staff_details?.in_work !== undefined && formData?.staff_details?.in_work === true}
                            />
                            <label htmlFor="is_in_work" className="text-sm text-gray-700">Employee is still in work</label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="radio"
                                id="is_not_in_work"
                                name="inWork"
                                className="mr-2"
                                onChange={(e) => handleInputUpdate('in_work', false)}
                                checked={formData?.staff_details?.in_work !== undefined && formData?.staff_details?.in_work === false}
                            />
                            <label htmlFor="is_not_in_work" className="text-sm text-gray-700">Employee is NOT still in work</label>
                        </div>
                    </div>
                    <div className="text-xs mt-2">
                        <span className="text-red-500">*</span> Denotes required fields.
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OHStaffDetails